import { fetchWards } from "api/location";
import {
  newsAdding,
  newsImageUpdate,
  newsUpdate,
  uploadImageToS3,
} from "api/news";
import PushNotification from "components/Modal/PushNotification";
import SubmitNewsArticle from "components/Modal/SubmitNewsArticle";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { retrieveUserData } from "utils/cryptoStorage";
import dateUtils from "utils/dateUtils";
import style from "../CreateNotification/create_noti.module.scss";
import Header from "./components/header";
import {
  base64ToBlob,
  draftValidation,
  validateForm,
} from "./components/helper";
import LeftSection from "./components/leftSection";
import RightSection from "./components/rightSection";

function CreateNews({
  setOpen,
  myNewsArticle,
  categoryData = [],
  setLoadCategory = () => {},
  isEdit,
  isDuplicate,
  handleTabChange = () => {},
  selectedNews,
  approval = false,
  setNews = () => {},
  setDetails = () => {},
  setLoadTable = () => {},
}) {
  const userToken = retrieveUserData();
  let admin_user_id, admin_user_name;

  if (userToken) {
    ({ admin_user_id, admin_user_name } = userToken);
  }

  const newsTitle = isEdit || isDuplicate ? selectedNews?.title : "";
  const messageContent = isEdit || isDuplicate ? selectedNews?.message : "";
  const abstractContent =
    isEdit || isDuplicate ? selectedNews?.abstract_content : "";
  const isTopNews = isEdit || isDuplicate ? selectedNews?.top_news : false;
  const newsSource = isEdit || isDuplicate ? selectedNews?.source : "";
  const authorName = isEdit || isDuplicate ? selectedNews?.author : "";
  const expiryDate =
    (isEdit || isDuplicate) &&
    selectedNews?.expirydatetime &&
    selectedNews?.expirydatetime !== "N/A"
      ? selectedNews?.expirydatetime
      : null;

  const [title, setTitle] = useState(newsTitle);
  const [message, setMessage] = useState(messageContent);
  const [abstract, setAbstract] = useState(abstractContent);
  const [topNews, setTopNews] = useState(isTopNews);
  const [source, setSource] = useState(newsSource);
  const [isLoading, setLoading] = useState(false);
  const [author, setAuthor] = useState(authorName);
  const [submit, setSubmit] = useState(false);
  const [expire, setExpire] = useState(true);
  const [schedule, setSchedule] = useState(!approval && myNewsArticle);
  const [expiry, setExpiry] = useState(isEdit && expiryDate);
  const [scheduleDate, setScheduleDate] = useState(
    isEdit && selectedNews?.scheduled_date
      ? dateUtils.getDateTime(selectedNews?.scheduled_date, "date")
      : null
  );
  const [scheduleTime, setScheduleTime] = useState(
    !approval && isEdit && selectedNews?.scheduled_date
      ? dateUtils.getDateTime(selectedNews?.scheduled_date, "time")
      : null
  );
  const [categoryId, setCategoryId] = useState(
    isEdit || isDuplicate ? selectedNews?.category_id : null
  );
  const [newsId, setNewsId] = useState(
    isEdit || isDuplicate ? selectedNews?.id : null
  );
  const [imageCaption, setImageCaption] = useState(
    isEdit
      ? selectedNews?.image_caption !== null
        ? selectedNews?.image_caption
        : ""
      : ""
  );
  const [recipientsData, setRecipientsData] = useState(
    isEdit || isDuplicate
      ? selectedNews?.wards?.length > 0
        ? selectedNews?.wards?.map((ward) => ({
            region_id: String(ward?.region_id),
            region_name: String(ward?.region_name),
            ward_code: String(ward?.ward_code),
            ward_name: String(ward?.ward_name),
          }))
        : [
            {
              label: "All citizens",
              value: "All citizens",
            },
          ]
      : []
  );
  const [recipientsFlag, setRecipientsFlag] = useState(
    isEdit || isDuplicate ? (selectedNews?.wards?.length > 0 ? 2 : 1) : null
  );
  const [newsImage, setNewsImage] = useState(
    isEdit ? selectedNews?.banner_img : null
  );
  const [authorImage, setAuthorImage] = useState(
    isEdit ? selectedNews?.author_image : null
  );
  const [newsStatus, setNewsStatus] = useState(0);
  // status 0-save as draft,1-submit,3-accepted,4-publish,5- reject
  const [recipients, setRecipients] = useState([]);

  useEffect(() => {
    if (selectedNews) {
      setNewsId(selectedNews.id);
    }
  }, [selectedNews]);

  useEffect(() => {
    if (!expire) {
      setExpiry(null);
    }
  }, [expire]);

  useEffect(() => {
    const loadWards = async () => {
      try {
        const wards = await fetchWards();
        const wardOptions = wards?.map((ward) => ({
          label: `${ward?.ward_code} - ${ward?.region_name}`,
          value: `${ward?.ward_code} - ${ward?.region_name}`,
          wardCode: Number(ward?.ward_code),
          wardName: ward?.ward_name,
          regionId: Number(ward?.region_id),
          regionName: ward?.region_name,
          wardStatusFlag: ward?.ward_status_flg,
        }));
        if (wardOptions?.length > 0) {
          setRecipients([
            {
              label: "All citizens",
              value: "All citizens",
            },
            ...wardOptions,
          ]);
        }
      } catch (error) {}
    };

    loadWards();
  }, [fetchWards]);

  const checkValidation = () => {
    // Scheduled submission stays in Drafts until EventBridge auto-submits it
    handleTabChange(myNewsArticle ? "1" : (schedule && !myNewsArticle ? "5" : "2"));
    setNewsStatus(myNewsArticle ? 4 : 1);
    const errors = validateForm(
      categoryId,
      title,
      abstract,
      message,
      author,
      source,
      newsImage,
      recipientsData,
      expiry,
      schedule,
      scheduleDate,
      myNewsArticle,
      scheduleTime
    );
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((errorMessage) => {
        toast.warning(errorMessage);
      });
    } else {
      setSubmit(true);
    }
  };

  const checkDraftValidation = () => {
    setNewsStatus(0);
    const errors = draftValidation(title, categoryId, recipientsFlag, expiry);
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((errorMessage) => {
        toast.warning(errorMessage);
      });
    } else {
      handleNewsSubmit("draft");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setNews(false);
    setDetails(false);
  };

  const handleNewsSubmit = async (type) => {
    setLoading(true);
    const formData = {
      news_category_id: Number(categoryId),
      news_title: title,
      abstract_content: abstract ? abstract : "",
      message_content: message,

      recipients_flg: recipientsFlag,
      news_source: source,
      author_name: author,
      news_status_flg: type === "draft" ? 0 : newsStatus,
      object_count:
        newsImage && authorImage ? 2 : newsImage || authorImage ? 1 : 0,
    };
    if (isEdit) {
      formData.modified_user_id = admin_user_id;
      formData.modified_user_name = admin_user_name;
    } else {
      formData.created_user_id = admin_user_id;
      formData.created_user_name = admin_user_name;
    }

    if (recipientsFlag === 2) {
      formData.wards = recipientsData;
    }

    if (topNews) {
      formData.top_news_flg = topNews;
    }
    if (expiry) {
      var date = dayjs(expiry, "DD/MM/YYYY");
      const formattedDate = date.format("YYYY-MM-DD");
      formData.expiry_date = formattedDate;
    }
    if (schedule) {
      const date = dayjs(scheduleDate, "DD/MM/YYYY");
      const formattedDate = date.format("YYYY-MM-DD");

      formData.is_scheduled_flg = 1;

      if (scheduleTime) {
        const formattedTime = dayjs(scheduleTime, "HH:mm").format("HH:mm");
        formData.scheduled_date = `${formattedDate} ${formattedTime}`;
      } else {
        formData.scheduled_date = formattedDate;
      }
    }
    let objectTypes = [];

    if (newsImage) {
      objectTypes.push(1);
    }

    if (authorImage) {
      objectTypes.push(2);
    }

    if (objectTypes.length > 0) {
      formData.object_types = objectTypes;
    }

    if (isEdit) {
      formData.news_id = Number(newsId);
    }

    try {
      const apiFunction = isEdit ? newsUpdate : newsAdding;
      const response = await apiFunction(formData);

      if (response.statusCode === 200) {
        const updates = [];

        const isFile = (image) => {
          return (
            image && (image.includes("data:image") || image instanceof Blob)
          );
        };

        // Handle news image upload if it's a file and different from the current image
        if (response?.data?.news_image_path && isFile(newsImage) && newsImage) {
          const newsImageBlob = base64ToBlob(newsImage);
          await uploadImageToS3(
            newsImageBlob,
            response?.data?.news_image_upload_path
          );
          updates.push({
            news_image_name: "newsImage",
            news_image_size: newsImageBlob?.size,
            news_image_mimetype: newsImageBlob?.type,
            news_image_filepath: response?.data?.news_image_path,
            image_type: 1,
            image_caption: imageCaption ? imageCaption : "",
          });
        }

        // Handle author image upload if it's a file and different from the current image
        if (
          response?.data?.author_image_path &&
          isFile(authorImage) &&
          authorImage
        ) {
          const authorImageBlob = base64ToBlob(authorImage);
          await uploadImageToS3(
            authorImageBlob,
            response?.data?.author_image_upload_path
          );
          updates.push({
            news_image_name: "authorImage",
            news_image_size: authorImageBlob?.size,
            news_image_mimetype: authorImageBlob?.type,
            news_image_filepath: response?.data?.author_image_path,
            image_type: 2,
            image_caption: "Author Image",
          });
        }

        // Update the database with the new image details if there are any updates
        if (updates.length > 0) {
          await newsImageUpdate({
            news_id: Number(response?.data?.news_id),
            is_insert: isEdit ? 0 : 1,
            images: updates,
          });
        }
        toast.success(response.message);
        handleClose();
      } else {
        toast.error(response.message);
      }
      setSubmit(false);
      setLoading(false);
      setDetails(false);
      setLoadTable((prev) => !prev);
    } catch (error) {
      toast.error(
        error.response ? error.response?.data?.message : error?.message
      );
      setSubmit(false);
      handleClose();
      setLoading(false);
    }
  };

  const disableExpiry = (current) => {
    const today = dayjs().startOf("day");
    const scheduleDate2 = scheduleDate
      ? dayjs(scheduleDate, "DD-MM-YYYY").endOf("day")
      : null;

    return (
      current &&
      (current < today || (scheduleDate2 && current <= scheduleDate2))
    );
  };
  const disableScheduleDate = (current) => {
    const today = dayjs().startOf("day");
    const expiryDate = expiry ? dayjs(expiry, "DD-MM-YYYY").endOf("day") : null;

    return current && (current < today || (expiryDate && current > expiryDate));
  };

  const handleImageUpdate = async (value, type, file) => {
    const imagePath =
      type === "news"
        ? selectedNews?.news_image_filepath
        : selectedNews?.author_image_path;

    if (isEdit && imagePath) {
      try {
        // Prepare the image data object
        const imageData = {
          news_image_name: file?.name,
          news_image_size: file.size ? file.size : 5000,
          news_image_mimetype: file.type ? file.type : "image/jpeg",
          news_image_filepath: imagePath,
          image_type: type === "news" ? 1 : 2,
        };

        // Optionally add the image caption if it exists
        if (type === "news") {
          imageData.image_caption = imageCaption ? imageCaption : "";
        }

        // Call the update function with the constructed image data
        const response = await newsImageUpdate({
          news_id: Number(selectedNews?.id),
          is_insert: value,
          images: [imageData],
        });

        // Check if the API response is successful
        toast.success(response?.message);
      } catch (error) {
        toast.error(`Failed to update image: ${error.message}`);
      }
    }
  };

  return (
    <>
      <div className="w-full create">
        {/* Page header*/}
        <Header
          handleClose={handleClose}
          isEdit={isEdit}
          myNewsArticle={myNewsArticle}
          newsStatus={newsStatus}
          isLoading={isLoading}
          handleTabChange={handleTabChange}
          checkDraftValidation={checkDraftValidation}
          checkValidation={checkValidation}
        />
        {/* Left Section : Category,ImageCaption,Image/Thumbnail, Author, Author image,
         Source, Recipients, Set an expiry date  */}
        <div className={`flex ${style["child-wrapper"]}`}>
          <LeftSection
            handleImageUpdate={handleImageUpdate}
            setCategoryId={setCategoryId}
            categoryId={categoryId}
            setLoadCategory={setLoadCategory}
            authorImage={authorImage}
            categoryData={categoryData}
            isEdit={isEdit}
            isDuplicate={isDuplicate}
            selectedNews={selectedNews}
            imageCaption={imageCaption}
            setImageCaption={setImageCaption}
            setAuthorImage={setAuthorImage}
            setNewsImage={setNewsImage}
            newsImage={newsImage}
            source={source}
            recipients={recipients}
            recipientsData={recipientsData}
            setRecipientsFlag={setRecipientsFlag}
            setRecipientsData={setRecipientsData}
            expire={expire}
            setExpire={setExpire}
            setExpiry={setExpiry}
            disableExpiry={disableExpiry}
            expiry={expiry}
            myNewsArticle={myNewsArticle}
            setScheduleDate={setScheduleDate}
            setScheduleTime={setScheduleTime}
            schedule={schedule}
            setSchedule={setSchedule}
            disableScheduleDate={disableScheduleDate}
            scheduleDate={scheduleDate}
            author={author}
            setAuthor={setAuthor}
            setSource={setSource}
            approval={approval}
          />
          {/* Left Section : Title, Message content, Abstract content, setting Top News  */}
          <RightSection
            setTitle={setTitle}
            title={title}
            message={message}
            setMessage={setMessage}
            abstract={abstract}
            setAbstract={setAbstract}
            topNews={topNews}
            setTopNews={setTopNews}
          />
        </div>
      </div>
      {myNewsArticle ? (
        <PushNotification
          title={"Publish News"}
          onSubmit={() => {
            setNewsStatus(4);
            handleNewsSubmit();
          }}
          content={`Are you sure you want to publish the news titled  '${title}'`}
          open={submit}
          setOpen={setSubmit}
          loading={isLoading}
          newsStatus={newsStatus}
        />
      ) : (
        <SubmitNewsArticle
          newsStatus={newsStatus}
          loading={isLoading}
          onSubmit={handleNewsSubmit}
          open={submit}
          setOpen={setSubmit}
        />
      )}
    </>
  );
}

export default CreateNews;
